import { Code, ConnectError } from "@connectrpc/connect";
import { redirect, useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { CreditCard } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { GetCreditCardsResponse } from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import ArrowBack from "~icons/material-symbols/arrow-back";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import ProgressActivity from "~icons/material-symbols/progress-activity";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import {
  AddCreditCardButton,
  CreditCardRow,
  creditCardRegistrationLimit,
} from "../../../features/charge/card-ui";
import { getChargeErrorMessage } from "../../../features/charge/charge-error-message";
import { getCreditCardsOrEmpty } from "../../../features/charge/credit-card-loader";
import { showGMOPGErrorToast } from "../../../features/charge/gmopg-error-toast";
import {
  canUseOsakiChargeBonusCharge,
  getCurrentOsakiChargeBonusCampaignStatus,
  getRemainingOsakiChargeBonusQuantity,
  osakiChargeBonusUnitPrice,
} from "../../../features/charge/osaki-charge-bonus-campaign";
import { consumePendingRegisteredCreditCard } from "../../../features/charge/pending-credit-card";
import {
  helperTextCss,
  pageCss,
  primaryButtonCss,
  secondaryButtonCss,
  sectionTitleCss,
} from "../../../features/charge/styles";
import type { WalletClient } from "../../../libs/client";
import { getGMOPGErrorInfoFromUnknown } from "../../../libs/connect";
import { getCurrentTime } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

type ChargeSearch = {
  amount?: number;
};

type LoaderData = {
  response: GetCreditCardsResponse;
};

const parseChargeAmount = (amount: unknown) =>
  Number(amount) === 10000 ? 10000 : 5000;

export const Route = createFileRoute("/_authed/charge/credit")({
  validateSearch: (search: Record<string, unknown>): ChargeSearch => ({
    amount: parseChargeAmount(search.amount),
  }),
  loaderDeps: ({ search }) => ({
    amount: parseChargeAmount(search.amount),
  }),
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.charge) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client, tenant }, deps }) => {
    const hasAwaitingMypaymentOrder =
      await hasAvailableAwaitingMypaymentOrder(client);
    if (tenant.slug === "miyagi" && hasAwaitingMypaymentOrder) {
      throw redirect({ to: "/charge" });
    }

    if (tenant.slug === "miyagi") {
      const [campaignStatuses, currentTime] = await Promise.all([
        client.getMyCampaignStatuses({}),
        getCurrentTime(),
      ]);
      const osakiChargeBonusStatus = getCurrentOsakiChargeBonusCampaignStatus(
        campaignStatuses,
        currentTime.date,
      );
      const quantity = deps.amount / osakiChargeBonusUnitPrice;
      const remainingQuantity = getRemainingOsakiChargeBonusQuantity(
        osakiChargeBonusStatus,
      );

      if (
        !canUseOsakiChargeBonusCharge(
          osakiChargeBonusStatus,
          currentTime.date,
        ) ||
        quantity > remainingQuantity
      ) {
        throw redirect({ to: "/charge" });
      }
    }

    return {
      response: (await getCreditCardsOrEmpty(client)).response,
    } satisfies LoaderData;
  },
  component: CreditCharge,
});

async function hasAvailableAwaitingMypaymentOrder(client: WalletClient) {
  try {
    const response =
      await client.getAvailableAwaitingPaymentMypaymentChargeOrder({});
    return !!response.order;
  } catch (error) {
    if (error instanceof ConnectError && error.code === Code.NotFound) {
      return false;
    }
    throw error;
  }
}

function CreditCharge() {
  const { response } = Route.useLoaderData();
  const { amount = 5000 } = Route.useSearch();
  const { client } = Route.useRouteContext();
  const navigate = useNavigate();
  const cards = useMemo(() => Object.values(response.cards), [response.cards]);
  const defaultCardId = response.defaultGmoCardId;
  const reachedCreditCardLimit = cards.length >= creditCardRegistrationLimit;
  const [selectedCardId, setSelectedCardId] = useState<string | null>(() => {
    const pendingCardId = consumePendingRegisteredCreditCard(amount, cards);
    return pendingCardId ?? defaultCardId ?? cards[0]?.gmoCardId ?? null;
  });
  const [loading, setLoading] = useState(false);

  const selectedCard = cards.find((card) => card.gmoCardId === selectedCardId);

  const charge = async () => {
    if (!selectedCard) {
      toast.error(t("クレジットカードを選択してください"));
      return;
    }

    setLoading(true);
    try {
      const chargeResponse = await client.chargeByCreditCard({
        price: BigInt(osakiChargeBonusUnitPrice),
        quantity: BigInt(amount / osakiChargeBonusUnitPrice),
        gmoCardId: selectedCard.gmoCardId,
        callbackUrlPath: "/charge/complete",
      });

      if (chargeResponse.tds2RedirectUrl) {
        window.location.href = chargeResponse.tds2RedirectUrl;
        return;
      }

      await navigate({ to: "/charge/complete" });
    } catch (error) {
      const gmoError = getGMOPGErrorInfoFromUnknown(error);
      if (gmoError) {
        showGMOPGErrorToast(gmoError);
        return;
      }
      showChargeError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        title={t("支払い情報")}
        onClickBack={() => navigate({ to: "/charge", search: { amount } })}
      />
      <main className={pageCss}>
        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          })}
        >
          <h2 className={sectionTitleCss}>{t("チャージ金額確認")}</h2>
          <div
            className={css({
              border: "1px solid",
              borderColor: "border.primary",
              rounded: "16px",
              px: "20px",
              py: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
            })}
          >
            <span
              className={css({
                fontSize: "14px",
                lineHeight: "1.4",
                color: "text.primary",
              })}
            >
              {t("チャージ金額")}
            </span>
            <span
              className={css({
                fontSize: "16px",
                lineHeight: "1.5",
                color: "text.primary",
              })}
            >
              {t("{amount} 円", { amount: formatNumber(amount) })}
            </span>
          </div>
        </section>

        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "8px",
          })}
        >
          <div
            className={css({
              w: "full",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            })}
          >
            <h2 className={sectionTitleCss}>
              {t("利用するクレジットカードを選択")}
            </h2>
            <p className={helperTextCss}>
              {t("チャージに使うクレジットカードを選択してください。")}
            </p>
            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              })}
            >
              {cards.map((card: CreditCard) => (
                <CreditCardRow
                  key={card.gmoCardId}
                  card={card}
                  selected={selectedCardId === card.gmoCardId}
                  isDefault={defaultCardId === card.gmoCardId}
                  onClick={() => setSelectedCardId(card.gmoCardId)}
                />
              ))}
            </div>
          </div>

          <AddCreditCardButton
            disabled={reachedCreditCardLimit}
            variant={cards.length === 0 ? "primary" : "secondary"}
            onClick={() =>
              navigate({ to: "/charge/credit-card/add", search: { amount } })
            }
          />

          {cards.length > 0 && (
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/charge/credit-card/manage",
                  search: { amount },
                })
              }
              className={css({
                p: "8px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                fontSize: "12px",
                lineHeight: "1.35",
                fontWeight: "600",
                color: "text.primary",
              })}
            >
              {t("登録済クレジットカードの管理")}
              <ArrowForward fontSize={16} />
            </button>
          )}
        </section>

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          })}
        >
          <button
            type="button"
            className={primaryButtonCss}
            disabled={loading || !selectedCard}
            onClick={charge}
          >
            {loading ? (
              <ProgressActivity
                className={css({ animation: "spin 1s linear infinite" })}
              />
            ) : (
              t("確定")
            )}
          </button>
          <button
            type="button"
            className={secondaryButtonCss}
            disabled={loading}
            onClick={() => navigate({ to: "/charge", search: { amount } })}
          >
            <ArrowBack />
            {t("もどる")}
          </button>
        </div>
      </main>
    </>
  );
}

function showChargeError(error: unknown) {
  toast.error(getChargeErrorMessage(error, t("チャージに失敗しました")));
}
